/**
 * Redux slice for Patient Resources state.
 *
 * Manages:
 * - Patient resources loading/ready state
 * - Error states for patient resources fetch
 *
 * Pattern: Uses createAsyncThunk for data fetching.
 * Components dispatch thunks instead of using useEffect.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchPatientResources as fetchPatientResourcesService } from '../../services/patientResourceService';
const initialState = {
    loading: false,
    ready: false,
    error: null
};
/**
 * Async thunk to fetch patient resources
 */
export const fetchPatientResources = createAsyncThunk('patientResources/fetchPatientResources', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ patientId, tenantId }, { rejectWithValue }) {
    try {
        yield fetchPatientResourcesService(Object.assign({ patientId }, (tenantId ? { tenantId } : {})));
        return true; // Success
    }
    catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch patient resources';
        return rejectWithValue(errorMessage);
    }
}));
const patientResourcesSlice = createSlice({
    name: 'patientResources',
    initialState,
    reducers: {
        /**
         * Set loading state
         */
        setLoading: (state, action) => {
            state.loading = action.payload;
            if (action.payload) {
                state.error = null;
                state.ready = false;
            }
        },
        /**
         * Set ready state after successful fetch
         */
        setReady: (state, action) => {
            state.ready = action.payload;
            state.loading = false;
            state.error = null;
        },
        /**
         * Set error state
         */
        setError: (state, action) => {
            state.error = action.payload;
            state.loading = false;
            state.ready = false;
        },
        /**
         * Clear error state
         */
        clearError: (state) => {
            state.error = null;
        },
        /**
         * Clear all patient resources state
         */
        clearPatientResources: () => {
            return Object.assign({}, initialState);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPatientResources.pending, (state) => {
            state.loading = true;
            state.error = null;
            state.ready = false;
        })
            .addCase(fetchPatientResources.fulfilled, (state) => {
            state.ready = true;
            state.loading = false;
            state.error = null;
        })
            .addCase(fetchPatientResources.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = false;
            state.ready = false;
        });
    }
});
export const { setLoading, setReady, setError, clearError, clearPatientResources } = patientResourcesSlice.actions;
export default patientResourcesSlice.reducer;
